import { useMemo } from 'react';
import Cookies from 'universal-cookie';
import { SHARED_USER_SEGMENTS } from './shared-user-segments';
import { useIsClient } from 'usehooks-ts';

const SEGMENT_ID_TO_KEY = Object.fromEntries(
  Object.entries(SHARED_USER_SEGMENTS).map(([key, id]) => [id, key as UserSegmentKey])
);

export type UserSegmentKey = keyof typeof SHARED_USER_SEGMENTS;

export type UserSegment = {
  id: string;
  key?: UserSegmentKey;
};

const isBrowser: boolean = typeof window !== 'undefined';

function getCookie(name: string, cookies?: string) {
  const cookie = new Cookies(cookies);
  return cookie.get(name) || '';
}

// cookie name for Treasure Data segments cookie
export const TDS_COOKIE_NAME = 'TDS';

const getSegmentCookies = () => {
  let TDSCookie = '';
  if (isBrowser) {
    TDSCookie = getCookie(TDS_COOKIE_NAME);
  }
  return { TDSCookie };
};

function mapSegmentIdsToKeys(segmentIds: string[]): UserSegment[] {
  const mappedSegments = segmentIds.map((id) => {
    const key = SEGMENT_ID_TO_KEY[id];
    return {
      id,
      key,
    };
  });
  return mappedSegments;
}

export const getUserSegmentsFromCookies = () => {
  const { TDSCookie } = getSegmentCookies();
  return TDSCookie.split(',').filter(Boolean);
};

export function getServerUserSegments(cookies: string): UserSegment[] {
  const tdsCookie = getCookie(TDS_COOKIE_NAME, cookies);

  return mapSegmentIdsToKeys(tdsCookie.split(',').filter(Boolean));
}

export const useUserSegments = () => {
  const isClient = useIsClient();
  // If we are on the server, there are no user segments
  // If we are on the client, read the cookies and parse the segments
  // We use useMemo to only compute this once per render
  // It is necessary to re-render on the client after hydration so that segments are read from cookies
  const segments = useMemo(() => {
    if (!isClient) {
      return [];
    }
    const userSegments = getUserSegmentsFromCookies();
    return mapSegmentIdsToKeys(userSegments);
  }, [isClient]);

  return segments;
};
